import { BasicUser } from "types/topicflow";

import Avatar from "@components/avatar/avatar";

export type OrbitCollaborator = {
  collaborationCount: number;
  externalEventCount: number;
  relationship: string;
  user: BasicUser;
};

const SIZE = 300;
const CENTRE = SIZE / 2;
// Floored so the closest avatar still clears the centre node, capped so the furthest stays inside.
const MIN_RADIUS = 66;
const MAX_RADIUS = 108;
const AVATAR_SIZE = 8; // h-8 w-8 = 32px
// Half the avatar (16px) plus a little air, so the count clears the avatar it belongs to.
const LABEL_OFFSET = 24;

const totalInteractions = (collaborator: OrbitCollaborator) =>
  collaborator.collaborationCount + collaborator.externalEventCount;

/**
 * The collaborator graph as an orbit: the subject sits in the middle and the people they work with
 * most sit closest, on a thicker link. Distance is the encoding, so this answers "who is in my
 * inner circle" at a glance — the list view is there for reading exact numbers.
 */
const WorkHistoryCollaboratorOrbit = ({
  collaborators,
  subjectLabel = "You",
}: {
  collaborators: OrbitCollaborator[];
  subjectLabel?: string;
}) => {
  const strongest = Math.max(
    1,
    ...collaborators.map((collaborator) => totalInteractions(collaborator)),
  );

  const nodes = collaborators.map((collaborator, index) => {
    const angle =
      -Math.PI / 2 + (index / Math.max(collaborators.length, 1)) * Math.PI * 2;
    const strength = totalInteractions(collaborator) / strongest;
    const radius = MIN_RADIUS + (1 - strength) * (MAX_RADIUS - MIN_RADIUS);
    return {
      collaborator,
      strength,
      x: CENTRE + Math.cos(angle) * radius,
      y: CENTRE + Math.sin(angle) * radius,
      // Counts sit further out along the same ray. Stacking them under the avatar puts the
      // closest node's label on top of the centre node.
      labelX: CENTRE + Math.cos(angle) * (radius + LABEL_OFFSET),
      labelY: CENTRE + Math.sin(angle) * (radius + LABEL_OFFSET),
    };
  });

  return (
    <div
      className="relative mx-auto"
      style={{ width: SIZE, height: SIZE }}
      aria-label="Top collaborators orbit"
    >
      <svg
        className="absolute inset-0"
        width={SIZE}
        height={SIZE}
        aria-hidden="true"
      >
        <circle
          cx={CENTRE}
          cy={CENTRE}
          r={MAX_RADIUS}
          fill="none"
          className="stroke-gray-100"
        />
        <circle
          cx={CENTRE}
          cy={CENTRE}
          r={(MAX_RADIUS + MIN_RADIUS) / 2}
          fill="none"
          className="stroke-gray-100"
        />
        {nodes.map((node) => (
          <line
            key={node.collaborator.user.id}
            x1={CENTRE}
            y1={CENTRE}
            x2={node.x}
            y2={node.y}
            className="stroke-tfcolor"
            strokeOpacity={0.2 + node.strength * 0.5}
            strokeWidth={1 + node.strength * 2}
          />
        ))}
      </svg>

      <div
        className="absolute flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-tfcolor text-xs font-medium text-white"
        style={{ left: CENTRE, top: CENTRE }}
      >
        {subjectLabel}
      </div>

      {nodes.map((node) => (
        <div key={node.collaborator.user.id}>
          <div
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: node.x, top: node.y }}
          >
            {/* Names aren't printed in this view, so the avatar tooltip carries them. */}
            <Avatar
              showTooltip
              size={AVATAR_SIZE}
              user={node.collaborator.user}
              className="ring-2 ring-white"
            />
          </div>
          <span
            className="absolute -translate-x-1/2 -translate-y-1/2 text-2xs tabular-nums text-gray-500"
            style={{ left: node.labelX, top: node.labelY }}
          >
            {totalInteractions(node.collaborator)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default WorkHistoryCollaboratorOrbit;
